/**
 * Protected route component for authentication-required routes
 */

import { forwardRef, useEffect } from 'react';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';
import { usePermissions } from '../hooks/usePermissions';
import type { ProtectedRouteProps } from '../types';

/**
 * Protected route component for wrapping routes that require authentication
 *
 * @example
 * ```tsx
 * <ProtectedRoute
 *   requireAuth
 *   requiredRoles={['admin']}
 *   redirectTo="/login"
 *   fallback={<div>Loading...</div>}
 * >
 *   <AdminDashboard />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute = forwardRef<HTMLDivElement, ProtectedRouteProps>(
  (props, ref) => {
    const {
      children,
      fallback = <div>Access Denied</div>,
      requireAuth = true,
      requiredRoles,
      requiredPermissions,
      redirectTo = '/login',
    } = props;

    // Get auth state from SDK
    const { isAuthenticated, isLoading } = useEnrichedAuth();
    const { canAccess } = usePermissions();

    useEffect(() => {
      if (!isLoading && requireAuth && !isAuthenticated && redirectTo) {
        // Redirect to login
        window.location.href = redirectTo;
      }
    }, [isLoading, isAuthenticated, requireAuth, redirectTo]);

    // Show loading state
    if (isLoading) {
      return <div ref={ref}>Loading...</div>;
    }

    // Not authenticated
    if (requireAuth && !isAuthenticated) {
      return <div ref={ref}>{fallback}</div>;
    }

    // Check role/permission requirements
    if (requiredRoles || requiredPermissions) {
      const hasAccess = canAccess(requiredRoles, requiredPermissions, true);

      if (!hasAccess) {
        return <div ref={ref}>{fallback}</div>;
      }
    }

    // Authenticated and authorized
    return <div ref={ref}>{children}</div>;
  }
);

ProtectedRoute.displayName = 'ProtectedRoute';

export default ProtectedRoute;
